import {inject, Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {CommunicationService} from '../../http/communication.service';
import {toPolarisOrigin} from '../polaris/polaris.utils';
import {
  PolarisApiChatMessagesResponseDto,
  PolarisApiChatOverviewDto,
  PolarisApiChatsOverviewResponseDto,
  PolarisApiGraphDto,
  PolarisApiMessageDto,
  PolarisApiSendMessageResponseDto,
  PolarisApiWidgetContextDto,
  PolarisChatAggregate,
  PolarisChatId,
  PolarisChatOverview,
  PolarisGraph,
  PolarisGraphSeries,
  PolarisRepositoryConversationHistoryMessage,
  PolarisTurn,
  PolarisWidgetContext,
  PolarisWizardVm
} from '../polaris/model';
import {
  getLegacyPolarisLambdaRequestId,
  getLegacyStatus,
  normalizeCompetitorsKeys,
  resolveSystemTimestamp,
  resolveUserTimestamp,
  toDomainCitations,
  toDomainCompetitorsKeys,
  toDomainModelVersion,
  toInsights,
  toStrictDateRange,
  toStringArray,
  toTurnStatus
} from './polaris-v2-utils';
import {
  GetChatByIdRequest,
  GetChatByIdResponse,
  GetChatsOverviewRequest,
  GetChatsOverviewResponse,
  SendMessageRequest,
  SendMessageResponse,
  UpdateChatTitleRequest
} from './polaris-v2.models';


@Injectable({
  providedIn: 'root'
})
export class PolarisV2Service {
  private readonly communicationService = inject(CommunicationService);
  private readonly conversationHistoryCache = new Map<PolarisChatId, PolarisTurn[]>();
  private readonly MAX_CACHED_CHATS = 2;
  private readonly API_PREFIX = '/polaris/v2'

  public async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    const timestamp = Date.now();
    const conversationHistory = this.buildConversationHistory(request.chatId);

    const body = {
      chatId: request.chatId,
      createNewChat: request.createNewChat ?? false,
      accountKey: request.accountKey,
      modelVersion: request.modelVersion,
      message: {
        brandKey: request.brandKey,
        competitorsKeys: normalizeCompetitorsKeys(request.competitorsKeys),
        widgetContext: this.toApiWidgetContext(request.widgetContext),
        dataDomain: request.dataDomain,
        userPrompt: request.userPrompt,
        timestamp,
        conversationHistory,
        debug: false,
        wizard: request.wizard,
        alert: request.alert,
        signal: request.signal
      }
    };

    const dto = await firstValueFrom(
      this.communicationService
        .httpPost<PolarisApiSendMessageResponseDto>(`${this.API_PREFIX}/chat/message`, body));

    const chat = this.toChatOverview(dto.chat);
    const widgetContext = this.toDomainWidgetContext(dto.message.widgetContext) ?? request.widgetContext;
    const messagePair = this.toMessagePair(dto.message, dto.chat.chatId, {
      userPrompt: request.userPrompt,
      userTimestamp: timestamp
    });

    const turn: PolarisTurn = {
      id: messagePair.id,
      chatId: dto.chat.chatId,
      status: toTurnStatus(getLegacyStatus(dto.message)),
      origin: widgetContext?.origin,
      widgetName: widgetContext?.name,
      widgetContext,
      myBrand: dto.message.brandKey ?? request.brandKey,
      myCompetitors: toDomainCompetitorsKeys(dto.message.competitorsKeys) ?? request.competitorsKeys,
      wizard: request.wizard,
      alert: request.alert,
      messagePair
    };

    this.updateCache(request.chatId, turn);
    return {chat, turn};
  }

  public async getChatsOverview(request: GetChatsOverviewRequest): Promise<GetChatsOverviewResponse> {
    const path = `${this.API_PREFIX}/accounts/${request.accountKey}/chats`;
    const dto = await firstValueFrom(
      this.communicationService.httpGet<PolarisApiChatsOverviewResponseDto>(path, {
        params: {limit: request.limit ?? 50, offset: request.offset ?? 0}
      })
    );
    return {
      items: dto.items.map(item => this.toChatOverview(item)),
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset
    };
  }

  public async getChatById(request: GetChatByIdRequest): Promise<GetChatByIdResponse> {
    const path = `${this.API_PREFIX}/accounts/${request.accountKey}/chats/${request.chatId}`;
    const dto = await firstValueFrom(
      this.communicationService.httpGet<PolarisApiChatMessagesResponseDto>(path, {params: {all: true}})
    );
    const chat = this.toChatAggregate(dto.chat, dto.messages);
    this.setCacheEntry(request.chatId, chat.turns);
    return {chat, pagination: dto.pagination};
  }

  public async updateChatTitle(request: UpdateChatTitleRequest): Promise<PolarisChatOverview> {
    const path = `${this.API_PREFIX}/accounts/${request.accountKey}/chats/${request.chatId}/title`;
    const dto = await firstValueFrom(
      this.communicationService.httpPut<PolarisApiChatOverviewDto>(path, {title: request.title})
    );
    return this.toChatOverview(dto);
  }

  public getWizardsResponse(): Observable<PolarisWizardVm[]> {
    return this.communicationService.httpGet<PolarisWizardVm[]>(`${this.API_PREFIX}/wizards-config`, {
      params: {full: true}
    });
  }

  private buildConversationHistory(chatId: PolarisChatId): PolarisRepositoryConversationHistoryMessage[] {
    return (this.conversationHistoryCache.get(chatId) ?? [])
      .filter(turn => turn.status !== 'failed')
      .flatMap(turn => {
        const messages: PolarisRepositoryConversationHistoryMessage[] = [
          {role: 'user', content: turn.messagePair.userPrompt}
        ];
        const assistantResponse = turn.messagePair.response.trim();
        if (assistantResponse) {
          messages.push({role: 'assistant', content: assistantResponse});
        }
        return messages;
      });
  }

  private updateCache(chatId: PolarisChatId, newTurn: PolarisTurn): void {
    const existing = this.conversationHistoryCache.get(chatId) ?? [];
    this.setCacheEntry(chatId, [...existing, newTurn]);
  }

  private setCacheEntry(chatId: PolarisChatId, turns: PolarisTurn[]): void {
    // Re-insert existing key to bump its recency in the Map's iteration order
    if (this.conversationHistoryCache.has(chatId)) {
      this.conversationHistoryCache.delete(chatId);
    } else if (this.conversationHistoryCache.size >= this.MAX_CACHED_CHATS) {
      const valueToDelete = this.conversationHistoryCache.keys().next().value;
      if (valueToDelete) {
        this.conversationHistoryCache.delete(valueToDelete);
      }
    }
    this.conversationHistoryCache.set(chatId, turns);
  }

  // --- DTO → domain mapping ---

  private toChatOverview(dto: PolarisApiChatOverviewDto): PolarisChatOverview {
    return {
      chatId: dto.chatId,
      accountKey: dto.accountKey,
      userId: dto.userId,
      title: dto.title ?? 'New Chat',
      parentChatId: dto.parentChatId,
      modelVersion: toDomainModelVersion(dto.modelVersion),
      createdAt: dto.createdAt,
      lastModified: dto.lastModified,
      lastMessageTime: dto.lastMessageTime
    };
  }

  private toChatAggregate(
    chatDto: PolarisApiChatMessagesResponseDto['chat'],
    messages: PolarisApiMessageDto[]
  ): PolarisChatAggregate {
    const overview = this.toChatOverview(chatDto);
    const turns: PolarisTurn[] = messages.map(message => {
      const widgetContext = this.toDomainWidgetContext(message.widgetContext);
      const messagePair = this.toMessagePair(message, overview.chatId);
      return {
        id: messagePair.id,
        chatId: overview.chatId,
        status: toTurnStatus(getLegacyStatus(message)),
        origin: widgetContext?.origin,
        widgetName: widgetContext?.name,
        widgetContext,
        myBrand: message.brandKey,
        myCompetitors: toDomainCompetitorsKeys(message.competitorsKeys),
        messagePair
      };
    });
    return {
      overview,
      turns,
      isThinking: turns.some(turn => turn.status === 'pending')
    };
  }

  private toMessagePair(
    dto: PolarisApiMessageDto,
    chatId: PolarisChatId,
    fallback: {userPrompt?: string; userTimestamp?: number} = {}
  ) {
    return {
      id: dto.id,
      chatId,
      userPrompt: dto.userPrompt ?? fallback.userPrompt ?? '',
      response: dto.response ?? '',
      status: getLegacyStatus(dto) ?? 'completed',
      userTimestamp: resolveUserTimestamp(dto, fallback.userTimestamp),
      systemTimestamp: resolveSystemTimestamp(dto),
      followUpQuestions: toStringArray(dto.followUpQuestions),
      insights: toInsights(dto.insights),
      creativesURLs: toStringArray(dto.creativesURLs),
      graphs: (dto.graphs ?? []).map(graph => this.toDomainGraph(graph)),
      originalResponse: dto.originalResponse,
      citations: toDomainCitations(dto.citations),
      webInsights: dto.webInsights,
      chatLibrary: dto.chatLibrary,
      polarisLambdaRequestId: getLegacyPolarisLambdaRequestId(dto)
    };
  }

  private toDomainGraph(dto: PolarisApiGraphDto): PolarisGraph {
    return {
      title: dto.title,
      type: dto.type,
      url: dto.url,
      x: dto.x,
      series: dto.series.map(s => this.toDomainSeries(s)),
      yIsPercentage: dto.y_is_percentage,
      xAxis: dto.x_axis,
      yAxis: dto.y_axis
    };
  }

  private toDomainSeries(dto: PolarisApiGraphDto['series'][number]): PolarisGraphSeries {
    return {name: dto.name, y: dto.y, unit: dto.unit};
  }

  private toApiWidgetContext(input: PolarisWidgetContext | undefined): PolarisApiWidgetContextDto | undefined {
    if (!input) return undefined;
    const dateRange = toStrictDateRange(input.dateRange);
    if (!input.origin || !input.name || !input.myBrand || !input.device || !dateRange) return undefined;
    return {
      origin: input.origin,
      name: input.name,
      metrics: input.metrics ?? [],
      myBrand: input.myBrand,
      myCompetitors: input.myCompetitors ?? [],
      dateRange,
      countries: input.countries ?? [],
      device: input.device
    };
  }

  private toDomainWidgetContext(input: PolarisApiWidgetContextDto | undefined): PolarisWidgetContext | undefined {
    if (!input) return undefined;
    return {
      origin: toPolarisOrigin(input.origin),
      name: input.name,
      metrics: input.metrics,
      myBrand: input.myBrand,
      myCompetitors: input.myCompetitors,
      dateRange: toStrictDateRange(input.dateRange),
      countries: input.countries,
      device: input.device
    };
  }
}
